import React, {useContext, useEffect, useState} from "react";
import Modal from "../../ui-components/Modal";
import {FormattedMessage, useIntl} from "react-intl";
import Spinner from "../../ui-components/Spinner";
import Button from "../../ui-components/Button";
import {ArrowDownTrayIcon, CheckIcon, PencilIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {NotificationsContext} from "../../ui-components/Notifications";
import Checkbox from "../../ui-components/Checkbox";
import Input from "../../ui-components/Input";
import useServiceAps from "../../hooks/useServiceAps";
import evaluateBlockDependencies from "../../utils/evaluateBlockDependencies";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import explodeSurveyResponses from "../../utils/explodeSurveyResponses";
import useSurveyApExport from "../../hooks/useConfigurationExport";
import convertSurveyResponses from "../../utils/convertSurveyResponses";
import prepareConfigurationStep from "../../utils/prepareConfigurationStep";
import _ from "lodash";
import useGetServiceConfiguration from "@hooks/useGetServiceConfiguration";
import {
  useCrossServicesConfigurations
} from "@feature/cross_services_configurations/hooks/useCrossServicesConfigurations";
import {prepareCrossData} from "../../utils/prepareCrossData";


const ActionButton = ({onClick, variant="empty", Icon}) => {
  return <Button
    styleType={variant}
    onClick={onClick}
    paddingCls="p-2"
  >
    <Icon className="w-5" />
  </Button>
}


const ExportSurveyModalEntry = ({ data, update, setEditing }) => {

  // hooks
  const intl = useIntl();

  // state
  const [localData, setLocalData] = useState(data);
  const [editMode, setEditMode] = useState(false);

  // utilities
  const confirmUpdate = () => {
    const editableFields = ["name"];
    const obj = editableFields.reduce(
      (p, c) => ({...p, [c]: localData[c]}),
      {}
    )
    update(obj);
  }
  const reset = () => {
    setLocalData(data);
    setEditMode(false);
  }

  useEffect(() => {
    reset();
  }, [data]);

  useEffect(() => {
    setEditing((prev) => prev + (editMode ? 1 : -1));
  }, [editMode]);

  return <>
    <div className="flex gap-4">
      <div>
        <Checkbox
          readOnly={false}
          id={`chk-ap-export-${data.idx}`}
          name={`chk-ap-export-${data.idx}`}
          checked={data.active}
          onChange={() => { update({active: !data.active}) }}
          classNames="-mt-0.5"
        />
      </div>
      <div className="flex flex-col w-full">
        <div className="flex items-end gap-3 w-full flex-1">
          <div className="flex-1">
            <Input
              label={intl.formatMessage({id: 'ap_name'})}
              value={localData.name}
              onChange={(e) => setLocalData((prev) => ({...prev, name: e.target.value}))}
              disabled={!editMode}
              hideBottomMargin={true}
            />
          </div>
          <div className="flex gap-1.5 mb-1">
            {
              editMode
                ? <>
                  <ActionButton onClick={confirmUpdate} Icon={CheckIcon} variant="greenWhite" />
                  <ActionButton onClick={reset} Icon={XMarkIcon} variant="redWhite" />
                </>
                : <ActionButton onClick={() => setEditMode(true)} Icon={PencilIcon} />
            }
          </div>
        </div>
        <div className="mt-2">
          <p className="text-base font-semibold mb-2">
            {data.title}
          </p>
          <p className="text-xs">
            {data.description}
          </p>
        </div>
      </div>
    </div>
    <hr className="my-3" />
  </>
}

const ExportSurveyModal = ({ survey, onDismiss }) => {

  // state
  const [entries, setEntries] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState(0);
  const [surveyData, setSurveyData] = useState(null);

  // hooks
  const intl = useIntl();
  const { push } = useContext(NotificationsContext);
  const countryApiRequest = useCountryAxiosInstance();
  const fetchServiceAps = useServiceAps(survey.service_id);
  const downloadApExport = useSurveyApExport(downloading, setDownloading);
  const getConfigurations = useGetServiceConfiguration(false, {
    survey_id: survey.id,
  });
  const {getCrossServicesConfigurations} = useCrossServicesConfigurations();
  // utilities
  const updateEntry = (idx, obj) => {
    if (!entries?.[idx])
      return;
    setEntries((prev) => prev.map((pe, i) => i === idx ? {...pe, ...obj} : pe))
  }
  const fetchSurveyData = async (s) => {
    const [configuration, responses, crossServicesConfigurations] = await Promise.all([
      getConfigurations(s.service_id, s.service_version),
      countryApiRequest.get("/service_survey_responses/all", {params: {service_survey_id: s.id}}),
      getCrossServicesConfigurations(s.service_id, s.service_version)
    ]);

    const responsesGroups = {};
    const {groups, containsRecursive} = explodeSurveyResponses(responses, configuration);
    groups.forEach((g, idx) => {
      const key = !containsRecursive ? s.id : `${s.id}_unnested-${idx}`;
      const obj = {surveyId: s.id, responses: g, created_at: s.created_at};
      responsesGroups[key] = obj;
    })

    // responses arr->obj
    const crossConfigurationsData = prepareCrossData(crossServicesConfigurations);
    const data = prepareConfigurationStep(configuration, undefined, s.service_id);
    // we identified that in case of NON recursive questions we have a response that is codified as string
    // and in this case we are double json parsing the value that is then passed to useconfigurationexport hook
    // where we then (again) use explodeSurveyResponses that evaluating the value again breaking
    // the dependenci evaluation (downloading an empty file )
    const convertedResponses = convertSurveyResponses(responses, {data, configuration, responses: {}, crossData: crossConfigurationsData}, true);

    return {configuration, responsesGroups, responses: convertedResponses};
  }
  const download = async () => {
    setDownloading(true)
    try {
      await downloadApExport(
        entries.filter(({active}) => active),
        survey,
        surveyData
      );
      onDismiss();
    }
    catch (e) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})})
      throw e;
    }
    finally {
      setDownloading(false)
    }
  }

  useEffect(() => {
    const fetch = async () => {
      // fetch data needed to evaluate dependencies
      const [{rows: serviceAps}, {configuration, responsesGroups, responses}] = await Promise.all([
        fetchServiceAps(null, null, 'asc', true, true),
        fetchSurveyData(survey),
      ]);
      // select APs that match the given dependencies
      const localEntries = serviceAps
        .filter(({dependencies}) => Object
          .values(responsesGroups)
          .map((rg) => rg.responses)
          .some((responses) => {
            const context = {configuration, responses};
            return evaluateBlockDependencies(dependencies, context, true);
          })
        )
        .map((sap, idx) => {
          return {
            idx: idx,
            id: sap.id,
            id_pa_item: sap.id_price_analysis_item,
            name: `NP${(idx + "").padStart(3, "0")}`,
            title: sap.des_title,
            description: sap.des_description,
            calculation: sap.calculation,
            dependencies: sap.dependencies,
            detail_multipliers: sap.items
              ?.map((i) => _.pick(i, ['id', 'val_parent', 'val_rel_posix', 'calculation']))
              ?? [],
            active: true,
          }
        });
      setEntries(localEntries);
      setSurveyData({configuration, responses});
    }

    fetch().then();
  }, []);

  return <Modal
    onExit={onDismiss}
  >
    <h1 className="text-center text-xl font-extrabold mt-3">
      {survey.name || <FormattedMessage id="unnamed_configuration" />}
    </h1>
    <h4 className="text-base my-3">
      <FormattedMessage id="select_ap_to_export" />
    </h4>
    {
      entries?.length > 0
        ? <div className="flex flex-col gap-2">
          {entries.map((e, idx) => <ExportSurveyModalEntry
            key={idx}
            data={e}
            update={(field, value) => updateEntry(idx, field, value)}
            setEditing={setEditing}
          />)}
        </div>
        : (
          !!entries
            ? <div className="flex items-center justify-center h-32 mb-4 text-gray-400">
              <FormattedMessage id="no_available_ap" />
            </div>
            : <div className="relative w-full py-10">
              <Spinner styleType="white" />
            </div>
        )
    }
    <div className="pt-1 flex justify-center">
      <Button
        onClick={download}
        submitting={downloading}
        disabled={!entries || entries.every((e) => !e.active) || editing > 0}
      >
        <ArrowDownTrayIcon className="w-4" />
        <FormattedMessage id="download" />
      </Button>
    </div>
  </Modal>
}

export default ExportSurveyModal;